/**
 * Fill planning: the seed block, the direction, and the cells to write
 * (Requirements 12.9, 3.1).
 *
 * {@link planFill} is pure. It takes the seed range, the range to fill, and the seed
 * block's typed text, and returns the rectangle to write plus the text for each cell
 * of it. The series itself comes from `series.ts`; this module only decides *which*
 * seeds feed *which* cells.
 *
 * The direction is read off the geometry rather than passed in, so one command shape
 * serves `Ctrl+D`, `Ctrl+R`, and a fill-handle drag in any of the four directions:
 * whichever edge of `to` extends past `from` is the direction, and the seeds are read
 * along that axis. A drag upward or leftward reads the seeds in reverse, so
 * `10, 20, 30` dragged up continues `0, -10` — the same step, the other way.
 *
 * Cells inside the seed block are never rewritten. That is what makes `Ctrl+D` over a
 * selection whose first row holds the seed a single command: `from` is the first row,
 * `to` is the whole selection, and only the rows below the seed are written.
 *
 * **Why the plan reports a source cell.** When a lane's seeds are not a series — the
 * `constant` and `cycle` cases — the fill is a *copy* of the seed block, repeated. A
 * copied formula must have its relative references offset (Requirement 4.12), and
 * that offset depends on which seed cell the text came from. So the plan names that
 * cell, and the caller (the command dispatcher, which owns the Formula_Engine) does
 * the translation. A detected numeric, date, or text series has no source cell: its
 * text is computed, not copied.
 */

import { normalizeRange, type Range } from '../types.js';
import { detectSeries, seriesAt } from './series.js';

/** Which way a fill runs. */
export type FillDirection = 'down' | 'up' | 'right' | 'left';

/** The seed cell a filled value was copied from, in absolute coordinates. */
export interface FillSource {
  row: number;
  col: number;
}

/** What a fill will write. */
export interface FillPlan {
  direction: FillDirection;
  /** The cells the fill writes, seed block excluded. */
  target: Range;
  /** Typed text per cell of `target`, row-major. */
  inputs: string[][];
  /**
   * The seed cell each value was copied verbatim from, or `null` when the value was
   * computed from a detected series.
   */
  sources: (FillSource | null)[][];
  /** Cells written. */
  cells: number;
}

/**
 * The direction implied by a seed block and a target, or `null` when the target
 * extends in no direction (it equals or sits inside the seed).
 *
 * A target that extends on two axes at once is filled along the longer extension,
 * because a fill handle dragged diagonally is a two-dimensional gesture with one
 * dominant intent, and continuing two series at once would need a per-axis step the
 * seeds do not carry.
 */
export function fillDirection(from: Range, to: Range): FillDirection | null {
  const f = normalizeRange(from);
  const t = normalizeRange(to);
  const down = t.r1 - f.r1;
  const up = f.r0 - t.r0;
  const right = t.c1 - f.c1;
  const left = f.c0 - t.c0;
  const best = Math.max(down, up, right, left);
  if (best <= 0) return null;
  if (best === down) return 'down';
  if (best === up) return 'up';
  if (best === right) return 'right';
  return 'left';
}

/** One lane's continuation: the text and, for a copy, the seed it came from. */
interface LaneValue {
  text: string;
  /** Index into the lane's seed list, or `null` for a computed series value. */
  seedIndex: number | null;
}

/**
 * Continues one lane of seeds.
 *
 * `constant` and `cycle` repeat the seed block, so each value names its seed; the
 * three detected series compute their values and name none.
 */
function continueLane(seeds: readonly string[], count: number): LaneValue[] {
  const series = detectSeries(seeds);
  const copies = series.k === 'constant' || series.k === 'cycle';
  const out: LaneValue[] = [];
  for (let n = 1; n <= count; n++) {
    out.push({
      text: seriesAt(series, n),
      seedIndex: copies && seeds.length > 0 ? (n - 1) % seeds.length : null,
    });
  }
  return out;
}

/**
 * Plans a fill (Requirement 12.9).
 *
 * `seedTexts` is the seed block's typed text, row-major over the normalized `from`
 * range. Returns `null` when there is nothing to fill.
 */
export function planFill(from: Range, to: Range, seedTexts: readonly string[][]): FillPlan | null {
  const f = normalizeRange(from);
  const t = normalizeRange(to);
  const direction = fillDirection(f, t);
  if (direction === null) return null;

  const rows = f.r1 - f.r0 + 1;
  const cols = f.c1 - f.c0 + 1;
  const seedAt = (i: number, j: number): string => seedTexts[i]?.[j] ?? '';
  const vertical = direction === 'down' || direction === 'up';

  if (vertical) {
    const count = direction === 'down' ? t.r1 - f.r1 : f.r0 - t.r0;
    const target: Range =
      direction === 'down'
        ? { sheet: f.sheet, r0: f.r1 + 1, r1: t.r1, c0: f.c0, c1: f.c1 }
        : { sheet: f.sheet, r0: t.r0, r1: f.r0 - 1, c0: f.c0, c1: f.c1 };
    // One lane per column, read top-to-bottom for a downward fill and
    // bottom-to-top for an upward one.
    const lanes = Array.from({ length: cols }, (_unused, j) => {
      const seeds: string[] = [];
      for (let i = 0; i < rows; i++) seeds.push(seedAt(direction === 'down' ? i : rows - 1 - i, j));
      return continueLane(seeds, count);
    });
    const inputs: string[][] = [];
    const sources: (FillSource | null)[][] = [];
    for (let n = 0; n < count; n++) {
      // An upward fill writes the furthest cell first, so the row nearest the seed
      // holds the first continuation.
      const step = direction === 'down' ? n : count - 1 - n;
      inputs.push(lanes.map((lane) => lane[step]?.text ?? ''));
      sources.push(
        lanes.map((lane, j) => {
          const seedIndex = lane[step]?.seedIndex;
          if (seedIndex === undefined || seedIndex === null) return null;
          const row = direction === 'down' ? f.r0 + seedIndex : f.r1 - seedIndex;
          return { row, col: f.c0 + j };
        }),
      );
    }
    return { direction, target, inputs, sources, cells: count * cols };
  }

  const count = direction === 'right' ? t.c1 - f.c1 : f.c0 - t.c0;
  const target: Range =
    direction === 'right'
      ? { sheet: f.sheet, r0: f.r0, r1: f.r1, c0: f.c1 + 1, c1: t.c1 }
      : { sheet: f.sheet, r0: f.r0, r1: f.r1, c0: t.c0, c1: f.c0 - 1 };
  const inputs: string[][] = [];
  const sources: (FillSource | null)[][] = [];
  for (let i = 0; i < rows; i++) {
    const seeds: string[] = [];
    for (let j = 0; j < cols; j++) seeds.push(seedAt(i, direction === 'right' ? j : cols - 1 - j));
    const lane = continueLane(seeds, count);
    const ordered = direction === 'right' ? lane : [...lane].reverse();
    inputs.push(ordered.map((value) => value.text));
    sources.push(
      ordered.map((value) => {
        if (value.seedIndex === null) return null;
        const col = direction === 'right' ? f.c0 + value.seedIndex : f.c1 - value.seedIndex;
        return { row: f.r0 + i, col };
      }),
    );
  }
  return { direction, target, inputs, sources, cells: count * rows };
}

/**
 * The range a fill-handle drag from `selection` to the hovered cell covers.
 *
 * Extends the selection along the axis the pointer travelled furthest, which is what
 * makes a slightly-off diagonal drag behave like the straight one the user meant.
 */
export function fillHandleTarget(selection: Range, row: number, col: number): Range {
  const s = normalizeRange(selection);
  const down = row - s.r1;
  const up = s.r0 - row;
  const right = col - s.c1;
  const left = s.c0 - col;
  const best = Math.max(down, up, right, left);
  if (best <= 0) return s;
  if (best === down) return { ...s, r1: row };
  if (best === up) return { ...s, r0: row };
  if (best === right) return { ...s, c1: col };
  return { ...s, c0: col };
}
